import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import "./Infrastructure.css";

import Airport from "../../assets/airport.webp";
import AtalSetu from "../../assets/atal-setu.webp";
import Jnpt from "../../assets/jnpt-highway.jpeg";
import Railway from "../../assets/railway-line.avif";
import SeaLink from "../../assets/sea-link.jpeg";
import Port from "../../assets/jnpt-port.jpg";
import Virar from "../../assets/virar-alibaug-multi.jpg";
import Ranjanpada from "../../assets/ranjanpada-railway-station.jpg";

const Infrastructure = () => {
  const containerRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const infoStages = [
    {
      title: "AIRPORT & CONNECTIVITY",
      items: [
        { title: "Navi Mumbai International Airport", img: Airport, desc: "A world-class aviation hub transforming regional connectivity." },
        { title: "Atal Setu (MTHL)", img: AtalSetu, desc: "India's longest sea bridge connecting Mumbai and Navi Mumbai." }
      ]
    },
    {
      title: "HIGHWAYS & RAILWAYS",
      items: [
        { title: "JNPT Highway", img: Jnpt, desc: "Critical artery for logistics and industrial growth." },
        { title: "The Nerul - Urban Railway Line", img: Railway, desc: "Seamless suburban transit for commuters." }
      ]
    },
    {
      title: "MARITIME & LOGISTICS",
      items: [
        { title: "Colaba - Urban Sea Link", img: SeaLink, desc: "Expanding coastal connectivity for faster travel." },
        { title: "JNPT Port", img: Port, desc: "India's premier container port driving international trade." }
      ]
    },
    {
      title: "FUTURE CORRIDORS",
      items: [
        { title: "Virar Alibaug Multi Modal Corridor", img: Virar, desc: "Upcoming mega-infrastructure project for the MMR." },
        { title: "Ranjanpada Railway Station", img: Ranjanpada, desc: "Strategic station boosting local accessibility." }
      ]
    }
  ];

  return (
    <section className="infra-section" ref={containerRef}>
      <div className="infra-sticky">
        <div className="infra-header">
           <motion.h2 
             style={{ 
               opacity: useTransform(smoothProgress, [0, 0.05], [1, 1]), // Always visible at start
               y: useTransform(smoothProgress, [0, 0.05], [0, 0]) // No y shift at start
             }}
           >
             Key Infrastructure Developments
           </motion.h2>
        </div>

        <div className="infra-content-track">
           {infoStages.map((stage, sIdx) => (
             <Stage key={sIdx} stage={stage} index={sIdx} scrollYProgress={smoothProgress} total={infoStages.length} />
           ))}
        </div>
      </div>
    </section>
  );
};

const Stage = ({ stage, index, scrollYProgress, total }) => {
  const start = index / total;
  const end = (index + 1) / total;
  
  // Adjusted opacity: first stage (index 0) starts at 1
  const opacity = useTransform(scrollYProgress, 
    [start, start + 0.05, end - 0.05, end], 
    [index === 0 ? 1 : 0, 1, 1, 0] 
  );
  
  // Adjusted y: first stage starts at 0
  const y = useTransform(scrollYProgress, 
    [start, start + 0.05, end - 0.05, end], 
    [index === 0 ? 0 : 50, 0, 0, -50]
  );

  const scale = useTransform(scrollYProgress, 
    [start, start + 0.05, end - 0.05, end], 
    [index === 0 ? 1 : 0.95, 1, 1, 0.95]
  );

  return (
    <motion.div 
      className="infra-stage"
      style={{ opacity, scale, y }}
    >
      <div className="infra-grid">
        {stage.items.map((item, iIdx) => (
          <div key={iIdx} className="infra-card">
            <div className="infra-img-box">
              <img src={item.img} alt={item.title} />
              <div className="infra-img-overlay" />
            </div>
            <div className="infra-info">
              <h3>{item.title}</h3>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default Infrastructure;
